/*
 * SPDX-FileCopyrightText: 2022 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

import { Injectable, inject } from '@angular/core';
import {
  BoelsBaseSite,
  CmsComponent,
  CmsNavigationComponent,
  NavigationNode,
} from '@boels/models';
import { Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { HttpService } from './http/http.service';
import { UrlService } from './url/url.service';

@Injectable()
export class NavigationService {
  private httpService = inject(HttpService);
  private urlService = inject(UrlService);

  activeBaseSite: BoelsBaseSite;
  lang: string;
  currency: string;

  /**
   * returns an observable with the `NavigationNode` for the given `CmsNavigationComponent`.
   * This function will load the navigation underlying entries and children if they haven't been
   * loaded so far.
   */
  public getNavigationNode(
    data$: Observable<CmsNavigationComponent>,
    activeBaseSite: BoelsBaseSite,
    lang: string,
    currency: string,
  ): Observable<NavigationNode> {
    if (!data$) {
      return of();
    }
    this.activeBaseSite = activeBaseSite;
    this.lang = lang;
    this.currency = currency;
    return data$.pipe(
      filter((data) => !!data),
      switchMap((data) => {
        const navigation = data.navigationNode ? data.navigationNode : data;
        const componentIds: string[] = [];
        navigation.children.forEach((component: CmsNavigationComponent) =>
          component.children.forEach((child: any) =>
            componentIds.push(child.entries[0].itemId),
          ),
        );
        return this.getNavigationEntryItems(componentIds.join(',')).pipe(
          map((items) => this.populateNavigationNode(navigation, items)),
        );
      }),
    );
  }

  /**
   * Create a new node tree for the view
   * @param nodeData
   * @param items
   */
  private populateNavigationNode(nodeData: any, items: any): NavigationNode {
    const node: NavigationNode = {};

    if (nodeData.title) {
      // the node title will be populated by the first entry (if any)
      // if there's no nodeData.title available
      node.title = nodeData.title;
    }

    // populate style classes to apply CMS driven styling
    if (nodeData.styleClasses) {
      node.styleClasses = nodeData.styleClasses;
    }
    // populate style attributes to apply CMS driven styling
    if (nodeData.styleAttributes) {
      node.styleAttributes = nodeData.styleAttributes;
    }

    if (nodeData.entries && nodeData.entries.length > 0) {
      this.populateLink(node, nodeData.entries[0], items);
    }

    if (nodeData.children?.length > 0) {
      const children = nodeData.children
        .map((child: any) => this.populateNavigationNode(child, items))
        .filter(Boolean);
      if (children.length > 0) {
        node.children = children;
      }
    }

    return node;
  }

  /**
   * The node link is driven by the first entry.
   */
  private populateLink(node: NavigationNode, entry: any, items: any) {
    const item = items.find((item: any) => item.uid === entry.itemId);

    // now we only consider CMSLinkComponent
    if (item && entry.itemType === 'CMSLinkComponent') {
      if (!node.title) {
        node.title = item.linkName;
      }
      const url = this.getLink(item);
      // only populate the node link if we have a visible node
      if (node.title && url) {
        node.url = url;
        // the backend provide boolean value for the target
        // in case the link should be opened in a new window
        if (item.target === 'true' || item.target === true) {
          node.target = '_blank';
        }
      }
      // populate style classes to apply CMS driven styling
      if (item.styleClasses) {
        node.styleClasses = item.styleClasses;
      }
      // populate style attributes to apply CMS driven styling
      if (item.styleAttributes) {
        node.styleAttributes = item.styleAttributes;
      }
    }
  }

  /**
   *
   * Gets the URL or link to a related item (category),
   * also taking into account content pages (contentPageLabelOrId)
   * and product pages (productCode)
   */
  protected getLink(item: any): string | string[] | undefined {
    if (item.url) {
      return item.url;
    }
    if (item.contentPageLabelOrId) {
      return item.contentPageLabelOrId;
    }
    return undefined;
  }

  private getNavigationEntryItems(ids: string): Observable<CmsComponent[]> {
    const url =
      this.urlService.combineUri(this.activeBaseSite.uid, 'cms', 'components') +
      `?componentIds=${ids}&lang=${this.lang}&curr=${this.currency}`;
    return this.httpService
      .get(url, [['pageSize', 30]])
      .pipe(map((result: any) => result.component));
  }
}
